import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Sparkles, TrendingUp, FileSearch, BarChart3, FileStack, Search, MessageSquare } from 'lucide-react';

export function CockpitGrid({ user, dashboardSummary, dailyQuestion, onNewConversation, onSetInput }) {
  const navigate = useNavigate();

  return (
    <div className="py-6 lg:py-10" data-testid="dashboard-cockpit">
      <div className="text-center mb-8 lg:mb-10">
        <div className="w-64 h-64 mx-auto mb-5 rounded-full overflow-hidden shadow-lg border-4 border-white dark:border-slate-600">
          <img src="/images/kai_full.jpeg" alt="KAI" className="w-full h-full object-cover" style={{ objectPosition: 'center 10%' }} />
        </div>
        <h3 className="font-heading text-2xl lg:text-3xl text-slate-700 dark:text-slate-200">
          Witaj, {user?.full_name?.split(' ')[0] || 'Uzytkowniku'}!
        </h3>
        <p className="text-sm lg:text-base text-slate-400 dark:text-slate-500 mt-2">
          Kliknij "Nowa Rozmowa" lub skorzystaj z narzedzi ponizej
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
        {/* Recent Law Changes */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-amber-500" />
            <h4 className="font-semibold text-lg text-slate-800 dark:text-white">Zmiany w przepisach</h4>
          </div>
          <div className="space-y-3 max-h-56 overflow-y-auto">
            {dashboardSummary?.law_changes?.length > 0 ? dashboardSummary.law_changes.map((c, i) => (
              <div key={i} className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300 line-clamp-2">{c.title}</p>
                {c.effective_date && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Od: {c.effective_date}</p>}
              </div>
            )) : (
              <p className="text-sm text-slate-400 text-center py-6">Brak nowych zmian w ostatnich 30 dniach</p>
            )}
          </div>
          <button
            onClick={() => navigate('/law-changes')}
            className="mt-3 text-sm text-[#1e3a5f] dark:text-blue-400 hover:underline font-medium"
          >
            Zobacz wszystkie zmiany
          </button>
        </div>

        {/* Quick Tools */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-blue-500" />
            <h4 className="font-semibold text-lg text-slate-800 dark:text-white">Narzedzia</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Sprawdz dokument', icon: FileSearch, path: '/review', color: 'text-violet-500' },
              { label: 'Porownaj podatki', icon: BarChart3, path: '/tax-comparison', color: 'text-green-500' },
              { label: 'Szablony pism', icon: FileStack, path: '/templates', color: 'text-blue-500' },
              { label: 'Analiza JPK', icon: Search, path: '/jpk', color: 'text-amber-500' },
            ].map((tool, i) => (
              <button
                key={i}
                data-testid={`quick-tool-${tool.path.slice(1)}`}
                onClick={() => navigate(tool.path)}
                className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all text-left"
              >
                <tool.icon className={`w-5 h-5 ${tool.color}`} />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{tool.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Stats + Daily Question */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-green-500" />
              <h4 className="font-semibold text-lg text-slate-800 dark:text-white">Twoje statystyki</h4>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Rozmowy', value: dashboardSummary?.stats?.conversations || 0 },
                { label: 'Pytania', value: dashboardSummary?.stats?.messages || 0 },
                { label: 'Ulubione', value: dashboardSummary?.stats?.favorites || 0 },
              ].map((s, i) => (
                <div key={i} className="text-center p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                  <p className="text-2xl font-bold text-[#1e3a5f] dark:text-blue-400">{s.value}</p>
                  <p className="text-sm text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {dailyQuestion && (
            <div
              data-testid="daily-question-widget"
              className="p-5 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl cursor-pointer hover:shadow-md transition-all"
              onClick={() => {
                onNewConversation();
                setTimeout(() => onSetInput(dailyQuestion.question), 100);
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold px-3 py-1 bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300 rounded-full">
                  Pytanie dnia
                </span>
                <span className="text-sm text-muted-foreground">{dailyQuestion.category}</span>
              </div>
              <p className="text-base font-medium text-primary-900 dark:text-primary-200">{dailyQuestion.question}</p>
              <p className="text-sm text-muted-foreground mt-1">Podpowiedz: {dailyQuestion.hint}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
